import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";

/**
 * usePushNotifications — gere o ciclo de vida das Web Push Notifications.
 *
 * Devolve:
 *   supported    — browser suporta push
 *   permission   — "default" | "granted" | "denied"
 *   subscribed   — tem subscrição activa no backend
 *   loading      — a processar
 *   subscribe()  — pede permissão e cria subscrição
 *   unsubscribe()— remove subscrição
 */
export function usePushNotifications() {
  const [supported, setSupported]   = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading]       = useState(true);

  // Verificar suporte e estado inicial
  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (!ok) { setLoading(false); return; }

    setPermission(Notification.permission);

    // Verificar estado no backend
    axios
      .get(`${API}/push/status`, { withCredentials: true })
      .then((r) => setSubscribed(r.data.subscribed))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Converter base64url → Uint8Array (para applicationServerKey)
  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    return new Uint8Array([...raw].map((c) => c.charCodeAt(0)));
  };

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: "unsupported" };
    setLoading(true);
    try {
      // 1. Pedir permissão
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        return { ok: false, reason: "denied" };
      }

      // 2. Obter VAPID public key
      const { data: { public_key } } = await axios.get(`${API}/push/vapid-public-key`);

      // 3. Registar SW e criar subscrição push
      const reg = await navigator.serviceWorker.ready;
      const pushSub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key),
      });

      // 4. Extrair chaves
      const rawSub = pushSub.toJSON();
      await axios.post(
        `${API}/push/subscribe`,
        {
          endpoint: rawSub.endpoint,
          p256dh:   rawSub.keys.p256dh,
          auth:     rawSub.keys.auth,
          user_agent: navigator.userAgent,
        },
        { withCredentials: true }
      );

      setSubscribed(true);
      return { ok: true };
    } catch (err) {
      console.error("[Push] Subscribe error:", err);
      return { ok: false, reason: err.message };
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const pushSub = await reg.pushManager.getSubscription();
      if (pushSub) {
        const rawSub = pushSub.toJSON();
        await pushSub.unsubscribe();
        await axios.delete(
          `${API}/push/unsubscribe`,
          {
            data: { endpoint: rawSub.endpoint, p256dh: rawSub.keys.p256dh, auth: rawSub.keys.auth },
            withCredentials: true,
          }
        );
      }
      setSubscribed(false);
    } catch (err) {
      console.error("[Push] Unsubscribe error:", err);
    } finally {
      setLoading(false);
    }
  }, [supported]);

  return { supported, permission, subscribed, loading, subscribe, unsubscribe };
}
